/* @classy/design-tokens — CSS-variable mirror of src/*.ts.
 * Keep values in lockstep with the TS source; src/tokens-css.test.ts
 * fails if they drift. Consumers import this file once at the root and
 * reference `var(--token-*)` in component CSS modules. */

:root {
  /* Colors */
  --token-accent: #e89b4c;
  --token-card-bg: #f5f4ed;
  --token-bg-dark: #0b0f12;
  --token-text-primary: #1e2a22;
  --token-chalkboard-blue: #0f869b;
  --token-chalkboard-green: #15bd6c;

  /* Typography */
  --token-font-body: 'Kiwi Maru', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --token-font-display:
    'Urbanist', 'Kiwi Maru', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  --token-font-ui: 'Montserrat', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;

  /* Radii */
  --token-radius-card: 28px;
  --token-radius-card-inner: 20px;
  --token-radius-pill: 12px;
  --token-radius-tag: 10px;
  --token-radius-drawing-pad: 8px;
  --token-radius-glass: 6px;
  --token-menu-h: 48px;

  /* Shadows */
  --token-shadow-card:
    0 110px 180px -30px rgba(8, 18, 12, 0.58), 0 60px 100px -16px rgba(8, 18, 12, 0.45),
    0 28px 48px -6px rgba(8, 18, 12, 0.36), 0 10px 20px rgba(8, 18, 12, 0.26),
    0 2px 4px rgba(8, 18, 12, 0.22), inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -2px 0 rgba(8, 18, 12, 0.08);
  --token-shadow-glass: 0 6px 24px rgba(0, 0, 0, 0.18);
  --token-shadow-drawing-pad: 0 2px 6px rgba(8, 18, 12, 0.08);

  /* Motion */
  --token-motion-scene-slide-ms: 1100ms;
  --token-motion-scene-slide-easing: cubic-bezier(0.8, 0, 0.2, 1);
  --token-motion-bg-crossfade-ms: 900ms;
  --token-motion-control-ms: 180ms;
  --token-motion-control-easing: cubic-bezier(0.4, 0, 0.2, 1);

  /* Spacing (4px scale) */
  --token-space-xs: 4px;
  --token-space-sm: 8px;
  --token-space-md: 12px;
  --token-space-lg: 16px;
  --token-space-xl: 24px;
  --token-space-2xl: 32px;
  --token-space-3xl: 48px;
  --token-space-4xl: 64px;

  /* Touch targets (kiosk requirements) */
  --token-touch-min: 80px;
  --token-touch-top-dead-zone-pct: 8%;
  --token-touch-safe-area-pct: 5%;

  /* ---------------------------------------------------------------
   * Responsive scale (TV / projector / laptop / 4K board)
   *
   * Classy runs on displays from a 1024x768 projector to a 3840x2160
   * Promethean board, in any aspect ratio. We do NOT design at one
   * fixed canvas and scale the whole thing (that letterboxes and
   * collides with edge chrome). Instead every page lays out fluidly
   * and derives its type + spacing from ONE knob: --ui-scale.
   *
   * --ui-scale is keyed to viewport HEIGHT (1.0 at 1080p) so an
   * ultra-wide-but-short display doesn't blow type up, and clamped so
   * a 720p projector isn't cramped and a 4K board isn't cartoonish.
   * (length / length -> unitless number; CSS Values L4, already used
   * by the old kiosk stage math.)
   * ------------------------------------------------------------- */
  --ui-scale: clamp(0.82, calc(100vh / 1080px), 1.7);

  /* Fluid type ramp — base px * --ui-scale. Reference sizes are tuned
   * for a 1080p TV; they shrink toward 720p and grow toward 4K. */
  --token-fs-3xs: calc(10px * var(--ui-scale));
  --token-fs-2xs: calc(12px * var(--ui-scale));
  --token-fs-xs: calc(14px * var(--ui-scale));
  --token-fs-sm: calc(16px * var(--ui-scale));
  --token-fs-base: calc(19px * var(--ui-scale));
  --token-fs-lg: calc(23px * var(--ui-scale));
  --token-fs-xl: calc(29px * var(--ui-scale));
  --token-fs-2xl: calc(38px * var(--ui-scale));
  --token-fs-3xl: calc(50px * var(--ui-scale));
  --token-fs-4xl: calc(66px * var(--ui-scale));
  --token-fs-5xl: calc(88px * var(--ui-scale));

  /* Fluid spacing — for page-level gaps/padding that should breathe
   * with the display. Component micro-padding can stay on the fixed
   * 4px scale above. */
  --token-space-fluid-sm: calc(8px * var(--ui-scale));
  --token-space-fluid-md: calc(14px * var(--ui-scale));
  --token-space-fluid-lg: calc(20px * var(--ui-scale));
  --token-space-fluid-xl: calc(32px * var(--ui-scale));
  --token-space-fluid-2xl: calc(48px * var(--ui-scale));

  /* ---------------------------------------------------------------
   * Live-mode safe area. Content centers INSIDE these insets so it
   * never slides under the fixed edge chrome (top-left back button +
   * corner mascot, left/right page chevrons, bottom nav/clock/menu) and
   * keeps a generous, even margin from the physical screen edge — the
   * projected morning-meeting surface should breathe, not crowd the
   * edges. Side inset (~9vw, floor 120px) clears both the 56px chevrons
   * AND the top-left corner mascot; top/bottom clear the back button and
   * the nav/clock/menu cluster with room to spare.
   * ------------------------------------------------------------- */
  --token-overscan: clamp(8px, 2.4vmin, 40px);
  /* Comfortable edge padding for the chrome-less phases (hub, settings)
   * — keeps content off the physical edge without reserving room for
   * live chrome that isn't there. */
  --token-edge-pad: clamp(20px, 3.5vmin, 72px);
  /* Top inset is lighter than the sides/bottom: the only top chrome is
   * the corner back button + mascot, which the side inset (--token-safe-x)
   * already clears horizontally — centered page content never reaches the
   * top corners. Keeping the top tight gives dense pages (e.g. Number)
   * the vertical room they need. The bottom stays generous to clear the
   * centered nav strip + clock + fullscreen. */
  --token-safe-top: max(var(--token-overscan), clamp(48px, 5.5vh, 104px));
  --token-safe-bottom: max(var(--token-overscan), clamp(76px, 8vh, 136px));
  --token-safe-x: max(var(--token-overscan), clamp(120px, 9vw, 240px));
}
